import ThronVideo from '@components/utils/ThronVideo';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@stone/ui/icon/Icon';
import '@stone/scss/components/editorial/_editorial_video.scss';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import classNames from 'classnames';
import { useLangChecker } from '@common/utils/i18n';
const EditorialVideo = (props) => {
    var _a, _b, _c, _d, _e;
    // ANCHOR - HOOKS --------------------------------------------------------------------------------------------
    const { t } = useTranslation();
    const { isDesktop } = useMediaQueries();
    const animation = {
        initial: { y: 0 },
        animate: { y: '100%' },
    };
    // ANCHOR - STATE --------------------------------------------------------------------------------------------
    const [isPlaying, setIsPlaying] = useState(((_a = props.overrideParams) === null || _a === void 0 ? void 0 : _a.play) || false);
    const [playedOnce, setPlayedOnce] = useState(false);
    const [videoStatus, setVideoStatus] = useState({
        currentTime: ((_b = props.overrideParams) === null || _b === void 0 ? void 0 : _b.currentTime) || 0,
        duration: ((_c = props.overrideParams) === null || _c === void 0 ? void 0 : _c.duration) || 0,
    });
    const [isCompleted, setIsCompleted] = useState(false);
    // const [props.fullScreen, props.setFullScreen] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [statusBarActive, setStatusBarActive] = useState(true);
    const [statusBarLocked, setStatusBarLocked] = useState(false);
    // const [placeholderHeight, setPlaceholderHeight] = useState(null);
    // ANCHOR - REF --------------------------------------------------------------------------------------------
    const buttonPlayRef = useRef(null);
    const firstRender = useRef(true);
    // const playedOnce = useRef(false);
    const statusbarRef = useRef(null);
    const playerRef = useRef(null);
    const trackRef = useRef(null);
    const overridedRef = useRef(false);
    const wrapperRef = useRef(null);
    // ANCHOR - MEMO --------------------------------------------------------------------------------------------
    const timing = useMemo(() => {
        const time = (videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.currentTime) > 0 ? Math.floor(videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.currentTime) : 0;
        const minutes = Math.floor(time / 60);
        const seconds = time - minutes * 60;
        if (!time)
            return null;
        return {
            minutes: minutes.toString().padStart(2, '0'),
            seconds: seconds.toString().padStart(2, '0'),
        };
    }, [videoStatus]);
    // ANCHOR - USE EFFECT --------------------------------------------------------------------------------------------
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
        }
    }, []);
    useEffect(() => {
        if (props.onUpdate) {
            props.onUpdate({ muted: isMuted, play: isPlaying, currentTime: videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.currentTime });
        }
    }, [isPlaying, isMuted, videoStatus]);
    useEffect(() => {
        var _a, _b, _c;
        if (props.isBackFromOverlay && playerRef.current && props.overrideParams) {
            playerRef.current.seek(((_a = props.overrideParams) === null || _a === void 0 ? void 0 : _a.currentTime) || 0);
            if ((_b = props.overrideParams) === null || _b === void 0 ? void 0 : _b.play) {
                playerRef.current.play();
                setIsPlaying(true);
            }
            if ((_c = props.overrideParams) === null || _c === void 0 ? void 0 : _c.muted) {
                playerRef.current.volume(0);
                setIsMuted(true);
            }
            if (props.overrideParams.currentTime) {
                playerRef.current.seek(props.overrideParams.currentTime);
                setVideoStatus((prev) => (Object.assign(Object.assign({}, prev), { currentTime: props.overrideParams.currentTime })));
            }
            props === null || props === void 0 ? void 0 : props.setIsBackFromOverlay(false);
        }
    }, [props.isBackFromOverlay, props.overrideParams]);
    useEffect(() => {
        var _a, _b, _c;
        if (props.isFullscreenOverlay && wrapperRef.current) {
            setTimeout(() => setStatusBarActive(false), 3000);
            let timer;
            const mouseCheck = (e) => {
                setStatusBarActive(true);
                clearTimeout(timer);
                timer = setTimeout(() => setStatusBarActive(false), 3000);
            };
            const handleFocusIn = (e) => {
                if (statusbarRef.current && statusbarRef.current.contains(e.target)) {
                    mouseCheck(e);
                    // Aggiungi qui la logica per gestire il focus sui figli
                }
            };
            (_a = statusbarRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('focusin', handleFocusIn);
            wrapperRef.current.addEventListener('mousemove', mouseCheck);
            if (isDesktop) {
                (_b = statusbarRef.current) === null || _b === void 0 ? void 0 : _b.addEventListener('mouseenter', () => setStatusBarLocked(true));
                (_c = statusbarRef.current) === null || _c === void 0 ? void 0 : _c.addEventListener('mouseleave', () => setStatusBarLocked(false));
            }
            return () => {
                var _a, _b, _c;
                (_a = statusbarRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('focusin', handleFocusIn);
                wrapperRef.current.removeEventListener('mousemove', mouseCheck);
                if (isDesktop) {
                    (_b = statusbarRef.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('mouseenter', () => setStatusBarLocked(true));
                    (_c = statusbarRef.current) === null || _c === void 0 ? void 0 : _c.removeEventListener('mouseleave', () => setStatusBarLocked(false));
                }
            };
        }
    }, [isDesktop]);
    // ANCHOR - FUNCTIONS --------------------------------------------------------------------------------------------
    const handleTrackClick = (e) => {
        let rect = e.target.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let progressBarPercentage = (x / e.target.clientWidth) * 100;
        let timeToSet = (progressBarPercentage / 100) * videoStatus.duration;
        playerRef.current.seek(timeToSet);
    };
    // const handleFullscreen = useCallback(() => {
    //   if (!props.fullScreen) {
    //     const region = document.querySelector('.experience-main');
    //     region.classList.add('editorial-video-regionfullscreen');
    //     document.body.classList.add('editorial-video-bodyfullscreen');
    //   } else {
    //     const region = document.querySelector('.experience-main');
    //     region.classList.remove('editorial-video-regionfullscreen');
    //     document.body.classList.remove('editorial-video-bodyfullscreen');
    //   }
    //   props.setFullScreen(!props.fullScreen);
    // }, [props.fullScreen]);
    // SECTION - RENDER --------------------------------------------------------------------------------------------
    const renderPlayButton = () => (React.createElement("button", { className: classNames('button button--pause', { inverted: props.isFullbleed || props.isFullscreenOverlay }), "aria-label": t('button.pause'), "data-pause": true, "aria-pressed": !isPlaying ? 'true' : 'false', onClick: () => {
            setIsPlaying(!isPlaying);
            if (!playedOnce) {
                setPlayedOnce(true);
            }
            if (isCompleted) {
                setIsCompleted(false);
            }
        }, ref: buttonPlayRef },
        React.createElement(Icon, { family: "16", name: isPlaying ? 'pause' : 'play', autoSize: true, inverted: props.isFullbleed || props.isFullscreenOverlay }),
        React.createElement("span", null, isPlaying ? t('button.pauseshort') : React.createElement("span", { ref: useLangChecker }, t('button.playshort')))));
    const renderStatusBar = () => (React.createElement("div", { className: classNames({ row: !playedOnce && !props.forceActiveState, fullscreen: props.isFullscreenOverlay || (props.isFullbleed && playedOnce) }, 'd-flex') },
        React.createElement("div", { className: classNames({ 'inner-col-8 inner-col-lg-3': !playedOnce && !props.isFullscreenOverlay && !props.forceActiveState }, 'd-flex') }, renderPlayButton()),
        playedOnce || props.isFullscreenOverlay || props.forceActiveState ? (renderControls()) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "inner-col-12 inner-col-lg-3" },
                React.createElement("p", { className: "font__subtitle" }, props.caption1)),
            isDesktop && props.caption2 && (React.createElement("div", { className: "inner-col-12 inner-col-lg-3" },
                React.createElement("p", { className: "font__subtitle" }, props.caption2))),
            React.createElement("div", { className: "inner-col-fill" },
                React.createElement("p", { className: "text-right" }, (timing === null || timing === void 0 ? void 0 : timing.minutes) && (timing === null || timing === void 0 ? void 0 : timing.seconds) ? (React.createElement(React.Fragment, null,
                    timing.minutes,
                    ":",
                    timing.seconds)) : (React.createElement(React.Fragment, null, "00:00"))))))));
    const renderStatusBarGallery = () => (React.createElement("div", { className: classNames('gallery-video--statusbar', { row: isDesktop }) },
        React.createElement("div", { className: classNames({ 'inner-col-lg-2': isDesktop }, 'gallery-video--play') },
            React.createElement("button", { className: 'button button--pause', "aria-label": t('button.pause'), "data-pause": true, "aria-pressed": "true", onClick: (e) => {
                    e.preventDefault();
                    setIsPlaying(!isPlaying);
                }, ref: buttonPlayRef },
                React.createElement(Icon, { family: "16", name: isPlaying ? 'pause' : 'play', autoSize: true, inverted: props.isFullbleed }),
                React.createElement("span", null, isPlaying ? t('button.pauseshort') : React.createElement("span", { ref: useLangChecker }, t('button.playshort'))))),
        React.createElement("div", { className: classNames({ 'inner-col-lg-4': isDesktop && !props.caption2, 'inner-col-2': isDesktop && props.caption2 }, 'gallery-video--caption1') },
            React.createElement("p", { className: "font__subtitle" }, props.caption1)),
        isDesktop && props.caption2 && (React.createElement("div", { className: "inner-col-lg-4 gallery-video--caption2" },
            React.createElement("p", { className: "font__subtitle" }, props.caption2))),
        React.createElement("div", { className: classNames({ 'inner-col-fill': isDesktop }, 'gallery-video--time') },
            React.createElement("p", { className: "text-right" }, (timing === null || timing === void 0 ? void 0 : timing.minutes) && (timing === null || timing === void 0 ? void 0 : timing.seconds) ? (React.createElement(React.Fragment, null,
                timing.minutes,
                ":",
                timing.seconds)) : (React.createElement(React.Fragment, null, "00:00"))))));
    const renderControls = () => (React.createElement("div", { className: "editorial-video-controls" },
        React.createElement("div", { className: "editorial-video-controls__audio" },
            React.createElement("button", { type: "button", onClick: () => {
                    if (isMuted) {
                        playerRef.current.volume(1);
                    }
                    else {
                        playerRef.current.volume(0);
                    }
                    setIsMuted(!isMuted);
                } },
                React.createElement(Icon, { family: "16", name: !isMuted ? 'speaker-on' : 'speaker-off', inverted: props.isFullbleed || props.isFullscreenOverlay || props.isFullscreenOverlay }))),
        React.createElement("div", { className: "editorial-video-controls__time" },
            React.createElement("span", { className: "font__title" }, (timing === null || timing === void 0 ? void 0 : timing.minutes) && (timing === null || timing === void 0 ? void 0 : timing.seconds) ? (React.createElement(React.Fragment, null,
                timing.minutes,
                ":",
                timing.seconds)) : (React.createElement(React.Fragment, null, "00:00"))),
            ' / ',
            React.createElement("span", { className: "font__title" }, (videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.duration) !== 0 ? (React.createElement(React.Fragment, null,
                Math.floor((videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.duration) / 60)
                    .toString()
                    .padStart(2, '0'),
                ":",
                Math.floor((videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.duration) % 60)
                    .toString()
                    .padStart(2, '0'))) : (React.createElement(React.Fragment, null, "00:00")))),
        React.createElement("div", { className: "editorial-video-controls__track-wrapper", onClick: handleTrackClick, "aria-hidden": true, ref: trackRef },
            React.createElement("div", { className: "editorial-video-controls__track", style: { width: `${((videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.currentTime) / (videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.duration)) * 100}%` } }),
            React.createElement("div", { className: "editorial-video-controls__track-progress", style: { left: `${((videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.currentTime) / (videoStatus === null || videoStatus === void 0 ? void 0 : videoStatus.duration)) * 100}%` } })),
        React.createElement("button", { type: "button", onClick: () => {
                props.setFullScreen(!props.fullScreen);
                setTimeout(() => {
                    playerRef.current.pause();
                    props.isFullscreenOverlay && props.setIsBackFromOverlay(true);
                }, 100);
            } },
            React.createElement(Icon, { family: "16", name: props.fullScreen ? 'collapse' : 'expand', inverted: props.isFullbleed || props.isFullscreenOverlay }))));
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "editorial-video" },
            React.createElement("div", { className: "video-container", ref: wrapperRef },
                React.createElement(ThronVideo, { thronID: props.thronId, thronMobileID: props.thronMobileId, muted: ((_d = props.overrideParams) === null || _d === void 0 ? void 0 : _d.muted) || false, autoplay: ((_e = props.overrideParams) === null || _e === void 0 ? void 0 : _e.play) || false, noskin: true, loop: false, lockBitrate: props.lockBitrate && 'max', onCompleteCallback: () => {
                        setIsPlaying(false);
                        setIsCompleted(true);
                        setPlayedOnce(false);
                    }, onPlayCallback: () => {
                        if (!playedOnce) {
                            setPlayedOnce(true);
                        }
                        setIsPlaying(true);
                        if (isCompleted) {
                            setIsCompleted(false);
                        }
                    }, onPauseCallback: () => !props.fullScreen && setIsPlaying(false), onInitCallback: (player) => {
                        var _a;
                        playerRef.current = player;
                        if (!(overridedRef === null || overridedRef === void 0 ? void 0 : overridedRef.current)) {
                            player.seek(((_a = props.overrideParams) === null || _a === void 0 ? void 0 : _a.currentTime) || 0);
                            overridedRef.current = true;
                        }
                        // if (props.overrideParams) {
                        //   player.seek(props.overrideParams?.currentTime || 0);
                        //   // if (props.overrideParams.play) {
                        //   //   player.play();
                        //   // }
                        // }
                        setVideoStatus((prev) => (Object.assign(Object.assign({}, prev), player.status())));
                    }, onTimeUpdate: (player) => {
                        setVideoStatus((prev) => (Object.assign(Object.assign({}, prev), player.status())));
                    } }),
                React.createElement(AnimatePresence, { exitBeforeEnter: true }, !props.isFullscreenOverlay && !playedOnce && !isPlaying && (React.createElement(motion.div, { initial: { opacity: firstRender.current ? 1 : 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, className: "poster__overlay" },
                    React.createElement("button", { tabIndex: -1, "aria-hidden": "true", className: "poster__circle-button", onClick: () => {
                            var _a;
                            (_a = buttonPlayRef === null || buttonPlayRef === void 0 ? void 0 : buttonPlayRef.current) === null || _a === void 0 ? void 0 : _a.click();
                        } },
                        React.createElement("span", { className: "font__title", ref: useLangChecker }, t('button.playshort')))))),
                React.createElement(motion.div, { ref: statusbarRef, className: classNames('editorial-video-statusbar', {
                        fullbleed: props.isFullbleed,
                        fullscreen: (props.isFullbleed && playedOnce) || props.isFullscreenOverlay,
                        'd-none': props.isFullbleed && !isPlaying && !isDesktop && !playedOnce && !props.isFullscreenOverlay && !props.forceActiveState,
                    }), initial: "initial", animate: !statusBarActive && props.isFullscreenOverlay && !statusBarLocked ? 'animate' : 'initial', variants: animation, transition: {
                        duration: 0.3,
                        ease: 'easeOut',
                    } },
                    React.createElement("div", { className: classNames({ container: props.isFullbleed || props.isFullscreenOverlay }) }, props.inGallery && !props.forceActiveState ? renderStatusBarGallery() : renderStatusBar()))))));
};
export default EditorialVideo;
